import moment from 'moment'
import { Cookies } from 'react-cookie'

import { sendNormalEvent } from '@/api/lineshop/tracking/sendNormalEvent'
import { sendProductsEvent } from '@/api/lineshop/tracking/sendProductsEvent'
import { COOKIE_DATA } from '@/constants/cookieKey'
import { handleSentryEvent } from '@/methods/handleSentryEvent'

import { LineShopEventId, LineShopProduct, NORMAL_EVENT_IDS, PRODUCT_EVENT_IDS } from './types'

type BaseArgs = {
  eventId: LineShopEventId
  redirectUrl: string
}

type TrackArgs = BaseArgs | (BaseArgs & { products: LineShopProduct[] })

const cookies = new Cookies()

export const readEcid = (): string | null => {
  if (typeof window === 'undefined') return null

  // doNotParse: 純數字 ecid 會被 universal-cookie 預設 JSON-parse 成 number，必須關掉
  const ecid = cookies.get<string>(COOKIE_DATA.LINE_ECID, { doNotParse: true })
  if (!ecid) return null

  const expireDatetime = cookies.get<string>(COOKIE_DATA.LINE_EXPIRE_DATETIME, { doNotParse: true })
  if (expireDatetime && moment(expireDatetime).isValid() && moment().isAfter(moment(expireDatetime))) {
    return null
  }

  return String(ecid)
}

const reportFailure = (eventId: LineShopEventId, error: unknown) => {
  const anyError = error as { response?: { status?: number; data?: unknown }; message?: string }

  handleSentryEvent({
    message: `[LINE_SHOP_TRACKING-${eventId}]`,
    level: 'warning',
    extra: {
      status: anyError?.response?.status,
      data: anyError?.response?.data,
      message: anyError?.message,
    },
  })
}

// fire-and-forget：呼叫端不應 await
// - 沒有有效 ecid → 直接 noop
// - event_id 與 endpoint 不符 → 直接 noop
// - 任何錯誤 → 寫 Sentry，不丟出例外
export const trackLineShopEvent = (args: TrackArgs): void => {
  const { eventId, redirectUrl } = args
  const products = 'products' in args ? args.products : undefined

  const ecid = readEcid()
  if (!ecid) return
  if (!redirectUrl) return

  const isNormalEvent = NORMAL_EVENT_IDS.includes(eventId)
  const isProductEvent = PRODUCT_EVENT_IDS.includes(eventId)

  const promise = (() => {
    if (isNormalEvent) {
      return sendNormalEvent({ event_id: eventId, ecid, redirect_url: redirectUrl })
    }

    if (isProductEvent) {
      if (!products?.length) return null
      return sendProductsEvent({ event_id: eventId, ecid, redirect_url: redirectUrl, products })
    }

    return null
  })()

  if (!promise) return

  promise.catch((error) => reportFailure(eventId, error))
}
